import apiClient from '../clienteFebosAPI';

const RESOURCE = '/herramientas';

// Constante con todas las rutas posibles para el servicio de documento digital
const DOC_DIGITAL_ROUTES = {
  // Endpoint base del proxy (lo que el frontend realmente llama)
  PROXY_BASE: `${RESOURCE}/externos/docdigital`,

  // Endpoints reales de la API de documento digital (lo que el proxy llama)
  REAL_API_ENDPOINTS: {
    // Método para cargar un documento firmado y despacharlo (envío oficial)
    SIGN_AND_DISPATCH: '/documentos/firmado/ingresar',
    // [ENDPOINT DEPRECADO] Método para cargar un documento firmado en estado borrador
    DRAFT_DOCUMENT: '/documentos/firmado/borrador',
    // Obtener las entidades asociadas al usuario actual
    MY_ENTITIES: '/api/entidades/token',
    MY_DOCUMENTS: '/api/tipos/documentos/',
  },

  // IDs de operación usados en las llamadas al apiClient
  OPERATION_IDS: {
    SEND_DNT_TO_DOC_DIGITAL: 'io.DocDigital.send',
    GET_FILE: 'io.docDigital.getFile',
    LIST_OPTIONS: 'io.docDigital.listOptions',
    UPDATE_FILE: 'docDigital.update',
    RECEIVED_DOCUMENTS: 'docDigital.recibidos',
    MY_ENTITIES: 'docDigital.myEntities',
    MY_DOCUMENTS: 'docDigital.myDocuments'
  }
};

const documentFromApi = (document) => ({
  ...(document.documentoPrincipal?.documentoId
    && { id: document.documentoPrincipal?.documentoId }),
  ...(document.documentoPrincipal?.materia && { theme: document.documentoPrincipal?.materia }),
  ...(document.documentoPrincipal?.descripcion
    && { description: document.documentoPrincipal?.descripcion }),
  ...(document.documentoPrincipal?.urlDocumento
    && { url: document.documentoPrincipal?.urlDocumento }),
  ...(document.documentoPrincipal?.tipoId && { type: document.documentoPrincipal?.tipoId }),
  ...(document.documentoPrincipal?.tipo
    && { typeDescription: document.documentoPrincipal?.tipo }),
  ...(document.documentoPrincipal?.fechaCreacion
    && { createdAt: document.documentoPrincipal?.fechaCreacion }),
  ...(document.documentoPrincipal?.folio && { folio: document.documentoPrincipal?.folio }),
  ...(document.infoCreador?.usuarioId && { creatorId: document.infoCreador?.usuarioId }),
  ...(document.infoCreador?.usuarioNombre
    && { creatorName: document.infoCreador?.usuarioNombre }),
  ...(document.infoCreador?.usuarioEmail
    && { creatorEmail: document.infoCreador?.usuarioEmail }),
  ...(document.infoCreador?.entidadId && { creatorEntityId: document.infoCreador?.entidadId }),
  ...(document.infoCreador?.entidadNombre
    && { creatorEntityName: document.infoCreador?.entidadNombre }),
  certifiers: (document.infoVisaciones?.visadores || []).flat().map((data) => ({
    ...(data.usuario_id && { id: data.usuario_id }),
    ...(data.usuario_nombre && { name: data.usuario_nombre }),
    ...(data.entidad_id && { entityId: data.entidad_id }),
    ...(data.entidad_nombre && { entityName: data.entidad_nombre }),
    ...(data.estado_proceso && { status: data.estado_proceso })
  })),
  signers: (document.infoFirmas?.firmantes || []).flat().map((data) => ({
    ...(data.usuario_id && { id: data.usuario_id }),
    ...(data.usuario_nombre && { name: data.usuario_nombre }),
    ...(data.entidad_id && { entityId: data.entidad_id }),
    ...(data.entidad_nombre && { entityName: data.entidad_nombre }),
    ...(data.estado_proceso && { status: data.estado_proceso })
  })),
  relatedDocuments: (document.documentosAnexos || []).map((data) => ({
    ...(data.anexoId && { id: data.anexoId }),
    ...(data.nombreArchivo && { name: data.nombreArchivo }),
    ...(data.urlDocumento && { url: data.urlDocumento }),
    ...(data.tipo && { type: data.tipo })
  })),
  ...(document.destinatarios?.conCopia
    && { withCopy: document.destinatarios?.conCopia }),
  ...(document.destinatarios?.usuariosDestinatarios
    && { targetUsers: document.destinatarios?.usuariosDestinatarios })
});

export const ddFiles = async (method, api, params) => {
  const requestParams = apiClient.queryParams({ metodo: method, api });

  let response = await apiClient.post(
    `${RESOURCE}/externos/docdigital?${requestParams}`,
    { params }, { operacionId: 'docDigital.recibidos' }
  );
  if (response.data.urlArchivoRespuesta) {
    response = await fetch(response.data.urlArchivoRespuesta);
    response = await response.json();
    const { respuesta: { result: documents } } = response;
    return documents.map((document) => documentFromApi(document));
  }
  const { data: { respuesta: { result: documents } } } = response;
  return documents.map((document) => documentFromApi(document));
};

export const oiDocDigitalGetFile = (payload) => {
  const params = apiClient.queryParams(payload.params);
  return apiClient.post(
    `${RESOURCE}/externos/docdigital/?${params}`,
    {
      url: payload.url,
      name: payload.name
    },
    { operacionId: 'io.docDigital.getFile' }
  );
};

export const listOptions = (payload) => {
  const { body } = payload;
  const params = apiClient.queryParams(payload.params);
  return apiClient.post(`${RESOURCE}/externos/docdigital/?${params}`,
    body,
    { operacionId: 'io.docDigital.listOptions' });
};

export const sendDntToDocDigital = (payload) => {
  const params = apiClient.queryParams(payload.params);
  return apiClient.post(`${RESOURCE}/externos/docdigital/?${params}`,
    payload.body,
    { operacionId: 'io.DocDigital.send' });
};

export const updateFileDD = async (method, api, params, cuerpo) => {
  const requestParams = apiClient.queryParams({ metodo: method, api });

  return apiClient.post(
    `${RESOURCE}/externos/docdigital?${requestParams}`,
    { params, cuerpo }, { operacionId: 'docDigital.update' }
  );
};

/**
 * Obtiene las entidades asociadas al usuario actual
 * @param {Object} params - Parámetros de paginación
 * @param {number} params.page - Número de página (default: 1)
 * @param {number} params.perPage - Elementos por página (default: 10)
 * @returns {Promise<Object>} Respuesta completa con result, total_count, total_pages, page
 */
export const getMyEntities = async ({ page = 1, perPage = 10 } = {}) => {
  const requestParams = apiClient.queryParams({
    metodo: 'get',
    api: DOC_DIGITAL_ROUTES.REAL_API_ENDPOINTS.MY_ENTITIES,
    page,
    per_page: perPage
  });

  const response = await apiClient.post(
    `${RESOURCE}/externos/docdigital/?${requestParams}`,
    { params: {} },
    { operacionId: DOC_DIGITAL_ROUTES.OPERATION_IDS.MY_ENTITIES }
  );

  // La respuesta puede venir en dos formatos:
  // 1. Con urlArchivoRespuesta (archivo JSON externo)
  // 2. Directamente en data.respuesta
  let respuesta;
  if (response.data.urlArchivoRespuesta) {
    const fileResponse = await fetch(response.data.urlArchivoRespuesta);
    const fileData = await fileResponse.json();
    respuesta = fileData.respuesta || fileData;
  } else {
    respuesta = response.data.respuesta || response.data;
  }

  // Devolvemos toda la respuesta para que el componente pueda usar paginación
  return respuesta;
};

/**
 * Obtiene los tipos de documentos disponibles
 * @param {Object} params - Parámetros de paginación
 * @param {number} params.page - Número de página (default: 1)
 * @param {number} params.perPage - Elementos por página (default: 10)
 * @returns {Promise<Object>} Respuesta con result, count, total_pages, page
 */
export const getMyDocuments = async ({ page = 1, perPage = 10 } = {}) => {
  const requestParams = apiClient.queryParams({
    metodo: 'get',
    api: DOC_DIGITAL_ROUTES.REAL_API_ENDPOINTS.MY_DOCUMENTS,
    page,
    per_page: perPage
  });

  const response = await apiClient.post(
    `${RESOURCE}/externos/docdigital/?${requestParams}`,
    { params: {} },
    { operacionId: DOC_DIGITAL_ROUTES.OPERATION_IDS.MY_DOCUMENTS }
  );

  // La respuesta puede venir en dos formatos:
  // 1. Con urlArchivoRespuesta (archivo JSON externo)
  // 2. Directamente en data.respuesta
  let respuesta;
  if (response.data.urlArchivoRespuesta) {
    const fileResponse = await fetch(response.data.urlArchivoRespuesta);
    const fileData = await fileResponse.json();
    respuesta = fileData.respuesta || fileData;
  } else {
    respuesta = response.data.respuesta || response.data;
  }

  // Devolvemos toda la respuesta para que el componente pueda usar paginación
  return respuesta;
};
